import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { ArrowLeft, Save, Bell, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'

interface AlertsProps {
  enabled: boolean
  webhookSet: boolean
  immediate: { masked: boolean; critical: boolean; lookbackMinutes: number }
  trend: {
    windowHours: number; minSessions: number; problemRate: number
    baselineMult: number; baselineDays: number; cooldownHours: number
  }
}

interface Props {
  version: number
  dataJson: string
  keyPlaceholder: string
  alerts: AlertsProps
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-foreground">{label}</span>
      {children}
      {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
    </label>
  )
}

function NumberInput({ value, onChange, step = 1, min, max }: { value: number; onChange: (v: number) => void; step?: number; min?: number; max?: number }) {
  return (
    <input
      type="number" value={value} step={step} min={min} max={max}
      onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
      className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
    />
  )
}

function AlertsSection({ alerts, keyPlaceholder }: { alerts: AlertsProps; keyPlaceholder: string }) {
  const [a, setA] = useState(alerts)
  const [webhook, setWebhook] = useState(alerts.webhookSet ? keyPlaceholder : '')

  const setTrend = (k: keyof AlertsProps['trend'], v: number) => setA({ ...a, trend: { ...a.trend, [k]: v } })
  const setImm = (k: keyof AlertsProps['immediate'], v: boolean | number) => setA({ ...a, immediate: { ...a.immediate, [k]: v } })

  const save = () => router.patch('/cc/eval/alerts', {
    alerts: {
      enabled: a.enabled,
      slack_webhook: webhook,
      immediate: { masked: a.immediate.masked, critical: a.immediate.critical, lookback_minutes: a.immediate.lookbackMinutes },
      trend: {
        window_hours: a.trend.windowHours, min_sessions: a.trend.minSessions, problem_rate: a.trend.problemRate,
        baseline_mult: a.trend.baselineMult, baseline_days: a.trend.baselineDays, cooldown_hours: a.trend.cooldownHours,
      },
    },
    // re-mask the webhook input so the saved secret is never left visible
  }, { preserveScroll: true, onSuccess: () => setWebhook(keyPlaceholder) })

  const sendTest = () => router.post('/cc/eval/alerts/test', {}, { preserveScroll: true })

  return (
    <div className="space-y-4 rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-semibold">Alertas de qualidade no Slack</h2>
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
          <input type="checkbox" checked={a.enabled} onChange={(e) => setA({ ...a, enabled: e.target.checked })} className="h-4 w-4" />
          {a.enabled ? 'Ativado' : 'Desativado'}
        </label>
      </div>
      <p className="text-xs text-muted-foreground">
        Proativo em sessões ruins de cliente, sem flood: alerta a transição (instância degradando ou sessão crítica),
        com cooldown e dedup. Roda a cada 15 min.
      </p>

      <Field label="Webhook do Slack (Workflow trigger)" hint="Mantido em segredo; deixe o placeholder para não alterar.">
        <input
          type="text" value={webhook} onChange={(e) => setWebhook(e.target.value)}
          placeholder="https://hooks.slack.com/triggers/…"
          className="w-full rounded-md border bg-background px-2 py-1.5 font-mono text-xs"
        />
      </Field>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-3 rounded-md border border-dashed p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Sessão crítica (imediato)</div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={a.immediate.masked} onChange={(e) => setImm('masked', e.target.checked)} className="h-4 w-4" />
            Sucesso falso (masked)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={a.immediate.critical} onChange={(e) => setImm('critical', e.target.checked)} className="h-4 w-4" />
            Severidade crítica
          </label>
        </div>

        <div className="space-y-3 rounded-md border border-dashed p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Tendência da instância</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Janela (h)"><NumberInput value={a.trend.windowHours} onChange={(v) => setTrend('windowHours', v)} min={1} max={168} /></Field>
            <Field label="Mín. sessões"><NumberInput value={a.trend.minSessions} onChange={(v) => setTrend('minSessions', v)} min={1} /></Field>
            <Field label="Taxa de problema" hint="0–1 (ex: 0.4 = 40%)"><NumberInput value={a.trend.problemRate} onChange={(v) => setTrend('problemRate', v)} step={0.05} min={0} max={1} /></Field>
            <Field label="× baseline" hint="acima da própria média"><NumberInput value={a.trend.baselineMult} onChange={(v) => setTrend('baselineMult', v)} step={0.1} min={1} /></Field>
            <Field label="Baseline (dias)"><NumberInput value={a.trend.baselineDays} onChange={(v) => setTrend('baselineDays', v)} min={1} max={90} /></Field>
            <Field label="Cooldown (h)"><NumberInput value={a.trend.cooldownHours} onChange={(v) => setTrend('cooldownHours', v)} min={1} max={168} /></Field>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={save} className="inline-flex items-center gap-1.5"><Save className="h-4 w-4" /> Salvar alertas</Button>
        <Button variant="outline" onClick={sendTest} className="inline-flex items-center gap-1.5"><Send className="h-4 w-4" /> Enviar teste</Button>
      </div>
    </div>
  )
}

export default function Settings({ version, dataJson, keyPlaceholder, alerts }: Props) {
  const [json, setJson] = useState(dataJson)
  const [parseError, setParseError] = useState<string | null>(null)

  const save = () => {
    try {
      JSON.parse(json)
      setParseError(null)
      router.patch('/cc/eval/settings', { data_json: json })
    } catch (e) {
      setParseError(String(e))
    }
  }

  return (
    <div className="h-full overflow-y-auto">
    <div className="mx-auto max-w-4xl space-y-4 p-6">
      <div>
        <Link href="/cc/eval" className="mb-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:underline">
          <ArrowLeft className="h-3 w-3" /> eval da frota
        </Link>
        <h1 className="text-xl font-semibold">Configuração do eval contínuo</h1>
        <p className="text-sm text-muted-foreground">
          Versão atual: v{version}. Os workers nas máquinas buscam esta configuração a cada ciclo
          (providers, model slots, lentes, budgets, amostragem e perfis por instância).
        </p>
      </div>

      <AlertsSection alerts={alerts} keyPlaceholder={keyPlaceholder} />

      <div className="rounded-lg border border-blue-300 bg-blue-50 p-3 text-xs text-blue-900 dark:bg-blue-950/30 dark:text-blue-200">
        <div className="mb-1 font-semibold">Configuração avançada (JSON)</div>
        <ul className="list-disc space-y-1 pl-4">
          <li><code>enabled: true</code> liga o julgamento em todas as máquinas; por instância use <code>instances.&lt;nome&gt;.enabled</code>.</li>
          <li><code>only_instances: ["livia-real-ventures"]</code> = modo canário — workers julgam SÓ as listadas; <code>[]</code> = frota toda.</li>
          <li>Keys com <code>keys: []</code> são injetadas das config vars <code>EVAL_&lt;PROVIDER&gt;_API_KEYS</code> (CSV: <code>EVAL_MINIMAX_API_KEYS</code>, <code>EVAL_OPENAI_API_KEYS</code>, <code>EVAL_LITELLM_API_KEYS</code>…) ao servir a config — colar keys aqui sobrescreve a env var.</li>
          <li>A/B de juiz: aponte as <code>lenses</code> pro model slot candidato (ex. <code>eval54</code>) + mude <code>judge_version</code> + <code>only_instances</code> com 1 instância — vereditos coexistem por versão e o dash compara via <code>?judge_version=</code>.</li>
          <li>Worker novo retoma do último veredito já no banco (<code>judged_through</code>); sem histórico, julga os últimos <code>sampling.backfill_days</code> dias (default 30).</li>
          <li>Keys de provider e webhook aparecem como <code>{keyPlaceholder}</code> e são preservados se não forem substituídos — cole um valor novo por cima para trocar.</li>
          <li><code>judge.budgets.openai_usd_per_day</code> é um teto rígido por máquina/dia — o worker pausa ao atingir.</li>
        </ul>
      </div>

      <textarea
        value={json}
        onChange={(e) => setJson(e.target.value)}
        spellCheck={false}
        className="h-[480px] w-full rounded-lg border bg-background p-3 font-mono text-xs"
      />
      {parseError && <p className="text-sm text-red-600">JSON inválido: {parseError}</p>}

      <Button onClick={save} className="inline-flex items-center gap-1.5">
        <Save className="h-4 w-4" /> Salvar (vira v{version + 1})
      </Button>
    </div>
    </div>
  )
}

Settings.layout = (page: React.ReactElement) => <CommandCenterLayout variant="fullscreen">{page}</CommandCenterLayout>
